<template>
	<ui-root v-if="loaded" :data="postcode" :part="form" ref="frm" :errs="errors" :actions="dialogControls(env)"></ui-root>
</template>

<script setup lang="ts">
import {onMounted, onUnmounted, ref, watch, reactive} from "vue";
import {connectEventBus, injectEnv} from "@fd/Environment";
import type {Postcode} from "@t/region/Postcode";
import type {WithMeta} from "@t/fd/WithMeta";
import {dialogControls, FormDefinition} from "@t/fd/layout/FormDefinition";
import type {ErrorMap} from "@fd/PartInjectKeys";
import {NetworkResponse} from "@fd/Types";
import {useRouter,useRoute} from "vue-router";
import {useNavigationStore} from "@/store/NavigationStore.ts";
import UiRoot from "@fd/containers/UiRoot.vue";
import {getIntAttParam} from "@/router/Router.ts";
import {InterfaceRoot} from "@t/fd/InterfaceRoot.ts";
import {useVirtualRoute} from "@/router/virtual/VirtualRouter.ts";


const env = injectEnv();
const navStore = useNavigationStore();
const router = useRouter();
const route = useVirtualRoute();

const loaded = ref<boolean>(false);

const errors = reactive<ErrorMap>([]);
const postcode = ref<Postcode|null>(null);
const form = ref<InterfaceRoot<FormDefinition>|null>(null);
const bus = connectEventBus("PostcodePage");
const frm = ref(null);

function receivedData(data:Postcode & WithMeta):void {
	if (data.META) {
		form.value = data.META.form;
		delete data.META;
	}
	postcode.value = data;
	loaded.value = true;
	if (postcode.value && postcode.value.id>0) {
		navStore.replace(postcode.value.zip);
	}
}

function loadData() {
	let endpoint = null;
	const id = route.query.id;
	if (id > 0) {
		endpoint = "api/postcodes/"+id+"?meta=true";
	}
	else {
		endpoint = "api/postcodes/init?meta=true";
	}
	return env.net.fetch(endpoint)
			.then((result) => result.json())
			.then(receivedData);
}

function saveData():Promise<NetworkResponse> {
    return env.net.fetch("api/postcodes/"+postcode.value.id, "PUT", postcode.value);
}

function createData():Promise<NetworkResponse> {
    return env.net.fetch("api/postcodes", "POST", postcode.value);
}

function sendData():Promise<NetworkResponse> {
	return (!postcode.value.id || postcode.value.id===0)?createData():saveData();
}

bus.onCommand(["save", "saveAndBack"], async (cmd) => {
	errors.length = 0;
	const response = await sendData();
	if (response.ok) {
		if (cmd === "save") {
			const data = await response.json();
			receivedData(data);
			if (frm.value) {
				frm.value.setDirty(false);
			}
		} else {
			// Go back
			router.back();
		}
		return "ok";
	} else {
		const data = await response.json();
		if (Array.isArray(data?.problems)) {
			for (let problem of data.problems) {
				errors.push(problem);
			}
		}
		console.error(response);
		return "fail";
	}
});

watch(() => route.query.id, loadData, { immediate: true });
navStore.defineMenu((mb) => mb.standardMenu());

onMounted(() => {
	navStore.forward(env.msg("menu.createPostcode"));
});
</script>

<style>

</style>
